import { useEffect, useRef, useState } from 'react'
import { Navigate, NavLink, useSearchParams } from 'react-router'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import SignInLoadingSpinner from '../login/SignInLoadingSpinner.tsx'
import GenericErrorPage from '../../pages/GenericErrorPage.tsx'
import { useSignInWithLink } from '../../app/hooks/UseSignInWithLink.ts'
import { setNeedsRegistration } from '../../features/FirebaseAuthSlice.ts'
import { useDispatch } from 'react-redux'
import {
  API_BASE_URL,
  useAcceptInviteMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { showGlobalModal } from '../../features/GlobalNotificationModalSlice.ts'

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const inviteCode = searchParams.get('code')
  const effectRan = useRef(false) // Track execution

  const dispatch = useDispatch()
  const [hasError, setHasError] = useState(false)
  const [loginState, signInWithLink] = useSignInWithLink()
  const [triggerAcceptInvite] = useAcceptInviteMutation()

  const [signUpComplete, setSignUpComplete] = useState(false)
  const { user } = useAppAuth()

  useEffect(() => {
    if (!inviteCode || effectRan.current) return
    effectRan.current = true // Mark as executed
    acceptInvite()
    // acceptInvite is a stable run-once handler guarded by effectRan; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode])

  const acceptInvite = async () => {
    if (!inviteCode) {
      setHasError(true)
      return
    }

    try {
      //1. Get email by invite code
      const response = await fetch(`${API_BASE_URL}/invite/email/${inviteCode}`)
      if (!response.ok) {
        console.error('Failed to fetch email by invite:', response)
        setHasError(true)
        return
      }

      const emailResponse = await response.json()
      if (
        !emailResponse ||
        !emailResponse.email ||
        emailResponse.email === ''
      ) {
        console.error('Failed to fetch email by invite, email is null or empty')
        setHasError(true)
        return
      }

      //2. Sign in with firebase
      //Prevent from requesting user after logging in with firebase
      dispatch(setNeedsRegistration(true))

      const userCredentials = await signInWithLink(emailResponse.email)
      const token = await userCredentials?.user.getIdToken(true)
      if (!token) {
        setHasError(true)
        return
      }

      //3. Send Accept Invite to Finalize the invite
      await triggerAcceptInvite({
        accessToken: token,
        inviteCode: inviteCode,
      }).unwrap()
    } catch (error) {
      console.error('Failed in acceptInvite process:', error)
      setHasError(true)
      return
    } finally {
      dispatch(setNeedsRegistration(false))
    }

    setSignUpComplete(true)

    dispatch(
      showGlobalModal({
        isOpen: true,
        title: 'Signup Complete!',
        description:
          "You have successfully accepted the invite and completed signup. You're all set to participate in upcoming events!",
        buttonText: 'Continue to club home',
        isSuccess: true,
      })
    )
  }

  if (loginState === 'FAILED' || hasError) {
    return (
      <GenericErrorPage
        title="Invitation Acceptance Failed"
        description="We couldn’t accept your invitation. The link may be invalid, expired, or already used (each link can only be used once). It’s also possible there was a temporary server error. Please request a new invitation or try another sign-in method.">
        <NavLink
          to="/login"
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Back to Login
        </NavLink>
      </GenericErrorPage>
    )
  }

  if (!signUpComplete || !user) {
    return <SignInLoadingSpinner message="Accepting invite, please wait..." />
  }

  if (user.clubs.length === 0) {
    return <Navigate replace to={`/clubs`} />
  }

  return <Navigate replace to={`/club/${user.clubs[0].id.toString()}`} />
}
